import { Add, Delete, Edit } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Avatar,
  Box,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import React, { useCallback, useEffect, useState } from "react";
import { QueryClient, useMutation } from "react-query";
import Search from "components/Search/Search";
import { handleError } from "helpers/utils";
import AddCompanyVendor from "./partials/AddCompanyVendor/AddCompanyVendor";
import DeleteCompanyVendor from "./partials/DeleteCompanyVendor/DeleteCompanyVendor";
import {
  ICompanyVendors,
  ICompanyVendorInput
} from "models/company";
import {
  CompanyVendorCells,
  addCompanyVendor,
  deleteCompanyVendor,
  editCompanyVendor,
  getCompanyVendors,
} from "framework/company";
import { useTranslation } from "react-i18next";
import { IDepartments } from "models/departments";
import { getDepartments } from "framework/departments";
import { IPermission } from "models/permissions";
import BlockIcon from "@mui/icons-material/Block";
import { getPermission, getUserData } from "helpers";
import EditCompanyVendor from "./partials/EditCompanyVendor/EditCompanyVendor";
import { IVendor } from "models/vendor";
import { getVendors } from "framework/vendor";
import useFetchSection from "hooks/useFetchSection";
interface Props {
  feature: Feature;
}

const CompanyVendors: React.FC<Props> = ({ feature }) => {
  const [loading2, setLoading2] = useState<boolean>(false);
  const [FilterCompanyVendors, setFilterCompanyVendors] = useState<
    ICompanyVendors[]
  >([] as ICompanyVendors[]);
  const { t, i18n } = useTranslation();
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [selectedCompanyVendor, setSelectedCompanyVendor] =
    useState<ICompanyVendors>({} as ICompanyVendors);
  const [inputObj, setInputObj] = useState<ICompanyVendorInput>(
    {} as ICompanyVendorInput
  );
  const [Vendors, setVendors] = useState<IVendor[]>(
    [] as IVendor[]
  );
  const { setAlert } = useAlert();
  const user = getUserData();
  const { colors } = user;
  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const addCompanyVendorMutation = useMutation({
    mutationFn: (createInput: ICompanyVendorInput) => {
      return addCompanyVendor(createInput);
    },
  });

  const editCompanyVendorMutation = useMutation({
    mutationFn: (createInput: ICompanyVendorInput) => {
      return editCompanyVendor(createInput, selectedCompanyVendor.id);
    },
  });

  const deleteDepartmentsMutation = useMutation({
    mutationFn: (id: string) => {
      return deleteCompanyVendor(id);
    },
  });

  const fetchVendors = useCallback(async () => {
    setLoading2(true);
    try {
      let response: any = await queryClient.fetchQuery("get vendors", () =>
        getVendors()
      );

      setVendors(response);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);


  useEffect(() => {
    fetchVendors();
    // eslint-disable-next-line
  }, []);


  const {
    loading,
    data: CompanyVendors,
    error,
    permission,
    revalidate,
  } = useFetchSection({
    fetchData: () => getCompanyVendors(),
    fetchPermission: () => getPermission(53),
    section: "Company Vendors",
    onSuccess: (data) => setFilterCompanyVendors(data),
  });


  if (loading)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleChange = (name: string, value: any) => {
    if (name == "active" && value == null) {
      value = false;
    }
    setInputObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOpenAdd = () => setOpenAdd(true);

  const handleOpenEdit = (Country: ICompanyVendors) => {
    setSelectedCompanyVendor(Country);
    setOpenEdit(true);
  };

  const handleOpenDelete = (Country: ICompanyVendors) => {
    setSelectedCompanyVendor(Country);
    setOpenDelete(true);
  };

  const handleCloseAdd = () => {
    setOpenAdd(false);
    setSelectedCompanyVendor({} as ICompanyVendors);
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedCompanyVendor({} as ICompanyVendors);
    setInputObj({} as ICompanyVendorInput);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedCompanyVendor({} as ICompanyVendors);
  };

  const getBodyCells = (row: ICompanyVendors) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "logo")
        return {
          id: "logo",
          element: (
            <TableCell align={"left"} key={i}>
              {row.logo ? (
                <Box
                  component={"img"}
                  src={row?.logo}
                  alt={row?.name}
                  width={50}
                  height={50}
                />
              ) : (
                <Avatar sx={{ bgcolor: "primary.main" }}>
                  {row?.name?.charAt(0)}
                </Avatar>
              )}
            </TableCell>
          ),
        };
      if (key === "name")
        return {
          id: "name",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.name}</Typography>
            </TableCell>
          ),
        };

      if (key === "active")
        return {
          id: "active",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.active ? 'Yes' : 'No'}</Typography>
            </TableCell>
          ),
        };

      if (key === "nameAr")
        return {
          id: "nameAr",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.nameAr}</Typography>
            </TableCell>
          ),
        };

      if (key === "countryName")
        return {
          id: "countryName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.countryName}</Typography>
            </TableCell>
          ),
        };
      // if (key === "customTitle")
      //   return {
      //     id: "customTitle",
      //     element: (
      //       <TableCell align={"left"} key={i}>
      //         <Typography variant="body2">{row?.customTitle}</Typography>
      //       </TableCell>
      //     ),
      //   };

      // if (key === "customTitleAr")
      //   return {
      //     id: "customTitleAr",
      //     element: (
      //       <TableCell align={"left"} key={i}>
      //         <Typography variant="body2">{row?.customTitleAr}</Typography>
      //       </TableCell>
      //     ),
      //   };

      if (key === "id")
        return {
          id: "actions",
          element: (
            <TableCell align={"left"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                spacing={1}
                justifyContent={i18n.language === "en" ? "start" : "end"}
              >
                {permission?.hasModifyPermission && (
                  <Tooltip title={t("T_edit")}>
                    <IconButton
                      size={"small"}
                      color={"primary"}
                      onClick={() => handleOpenEdit(row)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}

                {permission?.hasDeletePermission && (
                  <Tooltip title={t("T_delete")}>
                    <IconButton
                      size={"small"}
                      sx={{
                        color: colors.deleteIconColor
                          ? colors.deleteIconColor
                          : "red",
                        "&:hover": { color: colors?.deleteIconHoverColor },
                      }}
                      onClick={() => handleOpenDelete(row)}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </TableCell>
          ),
        };
    });
    return cells;
  };

  const handleAdd = async () => {
    if (!inputObj.vendorId)
      return setAlert({
        open: true,
        message: "Please fill all fields",
        type: "error",
      });
    try {
      setLoading2(true);

      await addCompanyVendorMutation.mutateAsync({ active: true, vendorId: inputObj.vendorId });
      setAlert({
        open: true,
        message: "Company Vendor added successfully",
        type: "success",
      });
      revalidate();
      fetchVendors();
      handleCloseAdd();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async () => {
    // if (
    //   CompanyVendors.find(
    //     (d) => d.departmentId === inputObj.departmentId
    //   ) &&
    //   inputObj.departmentId !== selectedCompanyVendor.departmentId
    // ) {
    //   setAlert({
    //     open: true,
    //     message: "Department Already Exists",
    //     type: "info",
    //   });
    //   return;
    // }
    try {
      setLoading2(true);
      await editCompanyVendorMutation.mutateAsync({
        vendorId: selectedCompanyVendor.vendorId,
        active: inputObj.active,
      });
      setAlert({
        open: true,
        message: "Company vendor Edit successfully",
        type: "success",
      });
      revalidate();
      fetchVendors();
      handleCloseEdit();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteDepartmentsMutation.mutateAsync(selectedCompanyVendor.id);
      setAlert({
        open: true,
        message: "Company vendor deleted successfully",
        type: "success",
      });
      revalidate();
      fetchVendors();
      handleCloseDelete();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleSearch = async (value: string) => {
    if (!value) return setFilterCompanyVendors(CompanyVendors!);
    let filtered = CompanyVendors!.filter((companyDepartment) =>
      (companyDepartment.name + " " + companyDepartment.nameAr).toLowerCase().includes(value.toLowerCase())
    );
    setFilterCompanyVendors(filtered);
  };

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Typography
            variant="h3"
            color={colors?.labelColor ? colors?.labelColor : "header.primary"}
          >
            {t(feature.name)}
          </Typography>
          <Stack direction={"row"} spacing={1}>
            <Search handleChange={handleSearch} />
            {permission?.hasModifyPermission && (
              <Tooltip title="Add Company Vendors">
                <IconButton
                  sx={{
                    color: colors.addUpdateIconColor
                      ? colors.addUpdateIconColor
                      : "red",
                    "&:hover": { color: colors?.addUpdateIconHoverColor },
                    height: 40,
                    width: 40,
                    transition: "0.5s",
                  }}
                  onClick={handleOpenAdd}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Grid>

        {permission?.hasReadPermission! &&
          FilterCompanyVendors.length > 0 && (
            <List
              headCells={CompanyVendorCells}
              data={FilterCompanyVendors}
              getBodyCells={getBodyCells}
              feature={feature}
              canEdit={false}
            />
          )}
        {(!FilterCompanyVendors || FilterCompanyVendors.length === 0) &&
          permission?.hasReadPermission! && (
            <EmptyContent
              icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
              header={t("T_no_vendor_header")}
              message={t("T_no_vendor_message")}
            />
          )}

        {!permission?.hasReadPermission && (
          <EmptyContent
            icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_permissions")}
            message={t("T_no_permissions_message")}
          />
        )}
      </Stack>

      {openAdd && (
        <AddCompanyVendor
          open={openAdd}
          loading={loading2}
          Vendors={Vendors}
          onClose={handleCloseAdd}
          onSubmit={handleAdd}
          onChanges={handleChange}
        />
      )}
      {openEdit && (
        <EditCompanyVendor
          open={openEdit}
          loading={loading2}
          companyVendor={selectedCompanyVendor}
          onChanges={handleChange}
          onClose={handleCloseEdit}
          onSubmit={handleEdit}
        />
      )}

      {openDelete && (
        <DeleteCompanyVendor
          open={openDelete}
          loading={loading2}
          onClose={handleCloseDelete}
          onSubmit={handleDelete}
        />
      )}
    </>
  );
};

export default CompanyVendors;
